import { fcrmCountryCodes } from "./fcrmCountryCode";

const GENDER_CODE = {
  Male: "M",
  Female: "F",
  M: "M",
  F: "F",
  U: "U",
  C: "C",
};

const text = (value) => String(value ?? "").trim();

const genderCodeFor = (gender) => GENDER_CODE[text(gender)] || "U";

export const formatDob = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const countryNamesFor = (client = {}) => {
  const otherIds = Array.isArray(client.otherIds) ? client.otherIds : [];
  return [
    client.residentialCountry,
    client.placeOfBirth,
    client.idIssueCountry,
    ...otherIds.map((item) => item?.idIssueCountry),
  ];
};

const subjectFrom = ({
  label,
  englishName,
  chineseName,
  gender,
  dob,
  client,
  countryNames,
  clientRef,
}) => {
  const name = text(englishName);
  const shown = text(label);
  if (!name) return { label: shown, ready: false, reason: "name" };

  const ref = text(clientRef != null ? clientRef : client?.code);
  if (!ref) return { label: shown || name, ready: false, reason: "code" };

  const request = {
    englishName: name,
    gender: genderCodeFor(gender),
    clientRef: ref,
  };
  const countryCodes = fcrmCountryCodes(countryNames);
  if (countryCodes.length) request.countryCodes = countryCodes;
  const chinese = text(chineseName);
  const birthday = formatDob(dob);
  if (chinese) request.chineseName = chinese;
  if (birthday) request.dob = birthday;
  return { label: shown || name, ready: true, request };
};

const normalizeKey = (value) => text(value).toLowerCase();

const contactRole = (title) => {
  const value = text(title).toLowerCase();
  const director = /\bdirector\b/.test(value);
  const shareholder = /\bshareholder\b/.test(value);
  const signatory = /\b(authori[sz]ed signatory|authori[sz]ed person)\b/.test(value);
  if (director && !shareholder && !signatory) return "D";
  if (signatory && !director && !shareholder) return "A";
  return "S";
};

const CONTACT_ROLE_ORDER = ["S", "D", "A"];

const partyRef = (clientCode, role, index) => {
  const suffix = index === 0 ? role : `${role}${index + 1}`;
  return `${clientCode}${suffix}`;
};

const personLabel = (english, chinese) => [text(english), text(chinese)].filter(Boolean).join(" · ");

const clientSubject = (client) => {
  if (client.type === "CORPORATE") {
    return subjectFrom({
      label: personLabel(client.name, client.cname),
      englishName: client.name,
      chineseName: client.cname,
      gender: "C",
      client,
      countryNames: [client.residentialCountry],
    });
  }

  const english = [text(client.surname), text(client.givenName)].filter(Boolean).join(" ");
  return subjectFrom({
    label: personLabel(english, client.cname),
    englishName: english,
    chineseName: client.cname,
    gender: client.gender,
    dob: client.birthday,
    client,
    countryNames: countryNamesFor(client),
  });
};

const contactSubject = (client, person, clientRef) =>
  subjectFrom({
    label: personLabel(person?.name, person?.cname),
    englishName: person?.name,
    chineseName: person?.cname,
    gender: person?.gender,
    dob: person?.birthday,
    client,
    clientRef,
    countryNames: [person?.residentialCountry],
  });

const relatedPersonSubjects = (client) => {
  const contacts = Array.isArray(client.contacts) ? client.contacts : [];
  const clientCode = text(client.code);
  const byRole = new Map();
  const unnamed = [];

  for (const person of contacts) {
    const english = text(person?.name);
    if (!english) {
      unnamed.push(person);
      continue;
    }
    const role = contactRole(person?.title);
    if (!byRole.has(role)) byRole.set(role, []);
    const list = byRole.get(role);
    const dedupeKey = [
      normalizeKey(english),
      normalizeKey(person?.cname),
      formatDob(person?.birthday),
    ].join("|");
    if (list.some((row) => row.dedupeKey === dedupeKey)) continue;
    list.push({ person, dedupeKey, sortName: normalizeKey(english) });
  }

  const related = [];
  for (const role of CONTACT_ROLE_ORDER) {
    const list = (byRole.get(role) || []).sort((left, right) =>
      left.sortName.localeCompare(right.sortName)
    );
    list.forEach((row, index) => {
      related.push(
        contactSubject(client, row.person, clientCode ? partyRef(clientCode, role, index) : "")
      );
    });
  }

  return [...related, ...unnamed.map((person) => contactSubject(client, person))];
};

export const nameScreeningSubjects = (client = {}) => [
  clientSubject(client),
  ...relatedPersonSubjects(client),
];

export const interpretNameScreening = (data = {}) => {
  const errorCode = text(data.errorCode);
  const errorMessage = text(data.errorMessage);
  if (!data.ok || errorCode || errorMessage) {
    return { status: "ERROR", errorCode, errorMessage };
  }

  const indicator = text(data.fecCheckIndicator).toUpperCase();
  const logId = data.logId ?? "";
  const alertId = data.alertId == null || data.alertId === "" ? "" : String(data.alertId);
  if (indicator === "Y") return { status: "HIT", logId, alertId };
  if (indicator === "N") return { status: "PASS", logId, alertId };
  return {
    status: "ERROR",
    errorCode: "",
    errorMessage: "",
  };
};

export const nameScreeningType = (value) =>
  text(value) === "On-going" ? "On-going" : "Initial";

export const nameScreeningRecord = (rows) =>
  (rows || [])
    .filter((row) => row?.outcome?.status)
    .map((row) => {
      const outcome = row.outcome;
      const record = {
        label: text(row.label),
        status: outcome.status,
        logId: outcome.logId == null ? "" : String(outcome.logId),
        alertId: text(outcome.alertId),
        type: nameScreeningType(outcome.type),
        errorCode: text(outcome.errorCode),
        errorMessage: text(outcome.errorMessage),
        screenedAt: outcome.screenedAt || new Date().toISOString(),
        ...(outcome.report ? { report: outcome.report } : {}),
      };
      if (outcome.status === "HIT") {
        record.handlingStatus = outcome.handlingStatus === "RESOLVED" ? "RESOLVED" : "PENDING";
        if (record.handlingStatus === "RESOLVED" && outcome.resolvedAt) {
          record.resolvedAt = outcome.resolvedAt;
        }
      }
      return record;
    });

const rowFromRecord = (record) => ({
  label: text(record.label),
  ready: true,
  outcome: {
    status: record.status,
    logId: record.logId ?? "",
    alertId: text(record.alertId),
    type: nameScreeningType(record.type),
    errorCode: text(record.errorCode),
    errorMessage: text(record.errorMessage),
    screenedAt: record.screenedAt || "",
    ...(record.status === "HIT"
      ? {
          handlingStatus: record.handlingStatus === "RESOLVED" ? "RESOLVED" : "PENDING",
        }
      : {}),
    ...(record.status === "HIT" &&
    record.handlingStatus === "RESOLVED" &&
    record.resolvedAt
      ? { resolvedAt: record.resolvedAt }
      : {}),
    ...(record.report ? { report: record.report } : {}),
  },
});

export const nameScreeningRowsFromRecord = (records) =>
  (records || [])
    .filter((record) => text(record?.status))
    .map(rowFromRecord)
    .sort((left, right) => {
      const leftTime = Date.parse(left.outcome.screenedAt) || 0;
      const rightTime = Date.parse(right.outcome.screenedAt) || 0;
      return rightTime - leftTime;
    });

export const formatScreenedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day} ${hours}:${minutes}`;
};

export const screenRecordStatusText = (row, t = (value) => value) => {
  const outcome = row?.outcome;
  if (!row?.ready) {
    return t(row?.reason === "code" ? "Client code is required" : "Name is required");
  }
  if (!outcome?.status) return t("Name is required");
  if (outcome.status === "ERROR") {
    return text(outcome.errorMessage) || text(outcome.errorCode) || t("Error");
  }
  if (outcome.status === "HIT") {
    return outcome.handlingStatus === "RESOLVED"
      ? t("Resolved")
      : t("Request further handling by Operation (any match result return)");
  }
  return t("Pass (no match result)");
};

export const handleMatchLabel = (row, { includeName = false } = {}) => {
  const when = formatScreenedAt(row?.outcome?.screenedAt);
  const logId = row?.outcome?.logId == null ? "" : String(row.outcome.logId);
  const base = [when, logId].filter(Boolean).join(" · ");
  if (includeName && text(row?.label)) return [base, text(row.label)].filter(Boolean).join(" · ");
  return base;
};

export const STRONG_DISCOUNT_FACTORS = [
  "English name mismatched",
  "Chinese name mismatched",
  "DOB mismatched",
  "YOB mismatched (difference of more than ±2 years)",
  "Parents mismatched",
  "Gender mismatched",
  "Place of birth mismatched",
  "Identity card number mismatched (same unique identity document type)",
];

export const SUPPORTING_DISCOUNT_FACTORS = [
  "Occupation mismatched",
  "Residency mismatched",
  "Nationality mismatched",
  "Citizenship mismatched",
  "Company name mismatched",
  "Address mismatched",
  "Marital status mismatched",
  "Spouse name mismatched",
  "Appearance/ Image mismatched",
];

export const discountFactorSelectData = (t) => [
  ...STRONG_DISCOUNT_FACTORS.map((factor) => ({
    value: factor,
    label: t(factor),
    group: t("Strong primary factor"),
  })),
  ...SUPPORTING_DISCOUNT_FACTORS.map((factor) => ({
    value: factor,
    label: t(factor),
    group: t("Supporting discount factor"),
  })),
];

export const commentParts = (hit = {}) =>
  [
    hit.discountFactor1,
    hit.discountFactor2,
    hit.otherDiscountFactor || hit.remark,
  ]
    .map((part) => text(part))
    .filter(Boolean);

export const commentSummary = (hit) => commentParts(hit).join(" · ");

export const matchReportFileFromUpload = (data, originalName = "") => {
  if (!data || typeof data !== "object") return null;
  const storedName = text(data.filename) || text(data.name);
  const name = text(originalName) || storedName;
  const cmIndex = text(data.cmIndex);
  let link = text(data.link) || text(data.path);
  let url = text(data.url);
  if (!link && !url && cmIndex && data._id) {
    link = `/api/file/cm/${data._id}`;
    url = link;
  }
  if (!link && !url && !cmIndex && !name) return null;
  return {
    ...(data._id ? { _id: data._id } : {}),
    name,
    filename: storedName || name,
    link,
    url,
    mimetype: text(data.mimetype) || "application/pdf",
    ...(data.storage ? { storage: data.storage } : {}),
    ...(cmIndex ? { cmIndex } : {}),
  };
};

export const canDownloadMatchReportFile = (report, hasLocalFile = false) =>
  hasLocalFile ||
  Boolean(
    text(report?.file?.link) ||
      text(report?.file?.url) ||
      text(report?.file?.cmIndex)
  );

export const normalizeMatchDecision = (value) => {
  if (value === "MATCH" || value === "MATCHED") return "MATCHED";
  if (value === "NOT_MATCH" || value === "NOT_MATCHED") return "NOT_MATCHED";
  if (value === "UNSURE") return "UNSURE";
  return "PENDING";
};

const normalizeLatinName = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractCjk = (value) =>
  String(value || "")
    .replace(/[^\u4e00-\u9fff]/g, "")
    .trim();

const nameTokens = (value) =>
  normalizeLatinName(value)
    .split(" ")
    .filter((token) => token.length > 1);

const latinNamesAlign = (left, right) => {
  const a = nameTokens(left);
  const b = nameTokens(right);
  if (!a.length || !b.length) return false;
  const setB = new Set(b);
  const overlap = a.filter((token) => setB.has(token)).length;
  if (overlap >= 2) return true;
  if (overlap === 1 && (a.length === 1 || b.length === 1)) return true;
  const joinedA = a.join(" ");
  const joinedB = b.join(" ");
  return joinedA.includes(joinedB) || joinedB.includes(joinedA);
};

const clientEnglishNames = (profile = {}) => {
  const surname = normalizeLatinName(profile.surname);
  const given = normalizeLatinName(profile.givenName);
  const whole = normalizeLatinName(profile.englishName || profile.name);
  const variants = new Set();
  if (surname && given) {
    variants.add(`${surname} ${given}`);
    variants.add(`${given} ${surname}`);
  } else if (whole) {
    variants.add(whole);
  }
  return [...variants];
};

const hitLatinNames = (hit) =>
  (hit?.names || []).map(normalizeLatinName).filter(Boolean);

const englishNameClearlyMismatched = (profile, hit) => {
  const clientNames = clientEnglishNames(profile);
  if (!clientNames.length) return false;
  const hitNames = hitLatinNames(hit);
  if (!hitNames.length) return false;
  return !hitNames.some((hitName) =>
    clientNames.some((clientName) => latinNamesAlign(clientName, hitName))
  );
};

const chineseNameClearlyMismatched = (profile, hit) => {
  const clientChinese = extractCjk(profile.cname);
  if (!clientChinese) return false;
  const hitChinese = (hit?.names || []).map(extractCjk).filter(Boolean);
  if (!hitChinese.length) return false;
  return !hitChinese.some(
    (name) => name === clientChinese || name.includes(clientChinese) || clientChinese.includes(name)
  );
};

const parseBirthParts = (value) => {
  const raw = text(value);
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [year, month, day] = raw.slice(0, 10).split("-").map(Number);
    return { year, month, day };
  }
  const slash = raw.match(/^(\d{4})(?:\/(\d{1,2}))?(?:\/(\d{1,2}))?$/);
  if (slash) {
    return {
      year: Number(slash[1]),
      month: slash[2] ? Number(slash[2]) : null,
      day: slash[3] ? Number(slash[3]) : null,
    };
  }
  const yearOnly = raw.match(/^(\d{4})$/);
  if (yearOnly) return { year: Number(yearOnly[1]), month: null, day: null };
  return null;
};

const sameCalendarDay = (left, right) =>
  Boolean(
    left?.year &&
      right?.year &&
      left.month &&
      left.day &&
      right.month &&
      right.day &&
      left.year === right.year &&
      left.month === right.month &&
      left.day === right.day
  );

const birthDatesOf = (value) =>
  text(value)
    .split(/\s*,\s*/)
    .map((part) => parseBirthParts(part))
    .filter(Boolean);

const birthClearlyMismatched = (profile, hit) => {
  const clientBirth = parseBirthParts(formatDob(profile.birthday));
  const hitBirths = birthDatesOf(hit?.birthDate);
  if (!clientBirth?.year || !hitBirths.length) return null;
  if (hitBirths.some((hitBirth) => sameCalendarDay(clientBirth, hitBirth))) {
    return null;
  }
  const fullHits = hitBirths.filter((hitBirth) => hitBirth.month && hitBirth.day);
  if (
    clientBirth.month &&
    clientBirth.day &&
    fullHits.length &&
    fullHits.every((hitBirth) => !sameCalendarDay(clientBirth, hitBirth))
  ) {
    return "DOB mismatched";
  }
  if (
    hitBirths.every((hitBirth) => Math.abs(clientBirth.year - hitBirth.year) > 2)
  ) {
    return "YOB mismatched (difference of more than ±2 years)";
  }
  return null;
};

const COUNTRY_NAME_TO_ISO = {
  "hong kong": "HK",
  china: "CN",
  taiwan: "TW",
  macau: "MO",
  singapore: "SG",
  japan: "JP",
  "united states": "US",
  "united kingdom": "GB",
  australia: "AU",
  canada: "CA",
};

const countryToIso = (value) => {
  const raw = text(value);
  if (/^[A-Z]{2}$/i.test(raw)) return raw.toUpperCase();
  return COUNTRY_NAME_TO_ISO[raw.toLowerCase()] || "";
};

const clientCountryCodes = (profile = {}) =>
  [profile.residentialCountry, profile.idIssueCountry, profile.countryOfBirth]
    .map(countryToIso)
    .filter(Boolean);

const hitCountryCodes = (hit) =>
  text(hit?.country)
    .split(/[,/]/)
    .map((part) => countryToIso(part) || text(part).toUpperCase())
    .filter((code) => /^[A-Z]{2}$/.test(code));

const countryClearlyMismatched = (profile, hit) => {
  const clientCodes = new Set(clientCountryCodes(profile));
  const hitCodes = hitCountryCodes(hit);
  if (!clientCodes.size || !hitCodes.length) return false;
  return !hitCodes.some((code) => clientCodes.has(code));
};

export const clientProfileForScreening = (client = {}, subjectLabel = "") => {
  const label = text(subjectLabel);
  const contacts = Array.isArray(client.contacts) ? client.contacts : [];
  const person = contacts.find((row) => {
    const english = text(row?.name);
    const chinese = text(row?.cname);
    const rowLabel = [english, chinese].filter(Boolean).join(" · ");
    return rowLabel === label || (english && english === label);
  });
  if (person) {
    return {
      surname: "",
      givenName: "",
      englishName: text(person.name),
      cname: text(person.cname),
      gender: person.gender,
      birthday: person.birthday,
      residentialCountry: text(person.residentialCountry) || client.residentialCountry,
      idIssueCountry: person.idIssueCountry || client.idIssueCountry,
      countryOfBirth: person.countryOfBirth || client.countryOfBirth,
    };
  }
  return client;
};

export const screeningHandlerProfile = (client = {}, subjectLabel = "") => {
  const profile = clientProfileForScreening(client, subjectLabel);
  const composed = [text(profile.surname), text(profile.givenName)]
    .filter(Boolean)
    .join(" ");
  return {
    englishName: composed || text(profile.englishName) || text(profile.name),
    chineseName: text(profile.cname),
    dateOfBirth: formatDob(profile.birthday),
    residentialCountry: text(profile.residentialCountry),
  };
};

export const suggestAutoMatchDecision = (
  client = {},
  hit = {},
  subjectLabel = ""
) => {
  const profile = clientProfileForScreening(client, subjectLabel);
  const strong = [];
  const supporting = [];

  if (englishNameClearlyMismatched(profile, hit)) {
    strong.push("English name mismatched");
  }
  if (chineseNameClearlyMismatched(profile, hit)) {
    strong.push("Chinese name mismatched");
  }
  const birthFactor = birthClearlyMismatched(profile, hit);
  if (birthFactor) strong.push(birthFactor);
  if (countryClearlyMismatched(profile, hit)) {
    supporting.push("Nationality mismatched");
  }

  const uniqueStrong = [...new Set(strong)];
  const uniqueSupporting = [...new Set(supporting)];

  if (uniqueStrong.length >= 1) {
    return {
      decision: "NOT_MATCHED",
      discountFactor1: uniqueStrong[0],
      discountFactor2: uniqueStrong[1] || uniqueSupporting[0] || "",
      otherDiscountFactor: "",
      remark: "",
    };
  }
  if (uniqueSupporting.length >= 2) {
    return {
      decision: "NOT_MATCHED",
      discountFactor1: uniqueSupporting[0],
      discountFactor2: uniqueSupporting[1],
      otherDiscountFactor: "",
      remark: "",
    };
  }
  return null;
};

const AUTO_DISCOUNT_FACTORS = new Set([
  "English name mismatched",
  "Chinese name mismatched",
  "DOB mismatched",
  "YOB mismatched (difference of more than ±2 years)",
  "Nationality mismatched",
]);

const markedOnlyByAutoDecide = (hit = {}) => {
  if (text(hit.otherDiscountFactor) || text(hit.remark)) return false;
  const factors = [hit.discountFactor1, hit.discountFactor2].map(text).filter(Boolean);
  return factors.length > 0 && factors.every((factor) => AUTO_DISCOUNT_FACTORS.has(factor));
};

export const applyAutoMatchDecisions = (
  client = {},
  hits = [],
  { onlyPending = true, subjectLabel = "" } = {}
) => {
  return (hits || []).map((hit) => {
    if (onlyPending && normalizeMatchDecision(hit.decision) !== "PENDING") {
      return hit;
    }
    const suggestion = suggestAutoMatchDecision(client, hit, subjectLabel);
    if (!suggestion) {
      if (!onlyPending && markedOnlyByAutoDecide(hit)) {
        return {
          ...hit,
          decision: "PENDING",
          discountFactor1: "",
          discountFactor2: "",
          otherDiscountFactor: "",
          remark: "",
        };
      }
      return hit;
    }
    return { ...hit, ...suggestion };
  });
};

const scoreSuffix = (hit) => {
  const parts = [
    hit.overallScore ? `overall ${hit.overallScore}` : "",
    hit.countryScore ? `ctry ${hit.countryScore}` : "",
    hit.birthDateScore ? `dob ${hit.birthDateScore}` : "",
    hit.nameScore ? `name ${hit.nameScore}` : "",
  ].filter(Boolean);
  return parts.join(", ");
};

const hitLine = (hit) =>
  [
    hit.matchListName,
    hit.mainSrcRefId,
    (hit.names || []).filter(Boolean).join(", "),
    hit.country,
    hit.birthDate,
    commentSummary(hit),
    scoreSuffix(hit),
  ]
    .map((part) => text(part))
    .filter(Boolean)
    .join(" | ");

export const matchReportNote = ({
  clientName = "",
  alertId = "",
  logId = "",
  screenedAt = "",
  report = {},
} = {}) => {
  const hits = Array.isArray(report?.hits) ? report.hits : [];
  const byDecision = (decision) =>
    hits.filter((hit) => normalizeMatchDecision(hit.decision) === decision);
  const matches = byDecision("MATCHED");
  const notMatches = byDecision("NOT_MATCHED");
  const unsure = byDecision("UNSURE");
  const pending = byDecision("PENDING");
  const screenedOn = formatScreenedAt(screenedAt);
  const lines = [
    "MBID name screening",
    clientName ? `Client: ${clientName}` : "",
    alertId ? `Alert ID: ${alertId}` : "",
    logId ? `Log ID: ${logId}` : "",
    screenedOn ? `Screened at: ${screenedOn}` : "",
    report?.uid ? `FCRM result: ${report.uid}` : "",
  ].filter(Boolean);
  lines.push(
    "",
    "Matched:",
    matches.length ? matches.map(hitLine).join("\n") : "(none)",
    "",
    "Not matched:",
    notMatches.length ? notMatches.map(hitLine).join("\n") : "(none)",
    "",
    "Unsure:",
    unsure.length ? unsure.map(hitLine).join("\n") : "(none)"
  );
  if (pending.length) lines.push("", `${pending.length} still pending`);
  return lines.join("\n");
};
